import React from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import LooksOneSharpIcon from '@mui/icons-material/LooksOneSharp';
import LooksTwoSharpIcon from '@mui/icons-material/LooksTwoSharp';
import LiveIndicator from '../components/LiveIndicator';
import { useSnackbar } from '../components/SnackbarContext';

const DECK_COLORS = {
  wssr: '#ff6600',
  wssr_node_2: '#92abcf',
};

function ScheduleCard() {
  const { combinedScheduleData } = useSnackbar();
  const loading = combinedScheduleData.length === 0;
  const count = combinedScheduleData.length;
  const tzAbbr = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value || '';

  return (
    <div style={{ marginBottom: '8rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <LiveIndicator />
      </div>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '760px', margin: '0 auto' }}>
          {Array.from(new Array(4)).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={14} height={14} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="30%" />
                <Skeleton variant="text" width="60%" height={28} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ maxWidth: '760px', margin: '0 auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)',
              gridTemplateRows: { xs: `repeat(${count}, minmax(110px, auto))`, md: `repeat(${count}, minmax(130px, auto))` },
            }}
          >
            {/* Decorative timeline spine: straight segments that break at each marker */}
            {combinedScheduleData.slice(0, -1).map((_, i) => (
              <Box
                key={`spine-${i}`}
                sx={{
                  gridColumn: 2,
                  gridRow: `${i + 1} / ${i + 3}`,
                  position: 'relative',
                  width: { xs: '3px', md: '4px' },
                  justifySelf: 'center',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: '32%',
                    bottom: '32%',
                    width: '100%',
                    backgroundColor: 'rgba(255,255,255,0.16)',
                  }}
                />
              </Box>
            ))}

            {combinedScheduleData.map((event, index) => {
              const eventStartTime = moment(event.start).local();
              const eventEndTime = event.end ? moment(event.end).local() : null;
              const now = moment();
              const isLive = !!event.is_now || (eventEndTime ? now.isBetween(eventStartTime, eventEndTime) : false);
              const isPast = !isLive && now.isAfter(eventStartTime);
              const color = DECK_COLORS[event.stationId] || DECK_COLORS.wssr;
              const contentOnSecondSide = index % 2 === 0;

              return (
                <React.Fragment key={`${event.stationId}-${event.start}-${index}`}>
                  {/* Reference dot, sitting directly on the spine */}
                  <Box
                    sx={{
                      gridColumn: 2,
                      gridRow: index + 1,
                      justifySelf: 'center',
                      alignSelf: 'center',
                      zIndex: 1,
                      width: isLive ? { xs: 18, md: 22 } : { xs: 13, md: 15 },
                      height: isLive ? { xs: 18, md: 22 } : { xs: 13, md: 15 },
                      borderRadius: 0,
                      backgroundColor: color,
                      opacity: isPast ? 0.4 : 1,
                      animation: isLive
                        ? `${event.stationId === 'wssr_node_2' ? 'pulse-dot-node2' : 'pulse-dot-wssr'} 1.6s infinite`
                        : 'none',
                    }}
                  />

                  {/* Show info, alternating sides of the spine */}
                  <Box
                    sx={{
                      gridColumn: contentOnSecondSide ? 3 : 1,
                      gridRow: index + 1,
                      display: 'flex',
                      justifySelf: contentOnSecondSide ? 'start' : 'end',
                      alignSelf: 'center',
                      padding: { xs: '0 0.5rem', md: '0 1rem' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        textAlign: 'left',
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        border: `1px solid ${isLive ? color : 'rgba(255,255,255,0.1)'}`,
                        borderRadius: '2px',
                        padding: { xs: '12px 16px', md: '16px 20px' },
                        minWidth: { xs: 150, md: 220 },
                        maxWidth: { xs: 230, md: 320 },
                        opacity: isPast ? 0.5 : 1,
                        boxShadow: isLive ? `0 8px 20px ${color}29` : 'none',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color,
                          marginBottom: '4px',
                        }}
                      >
                        {event.stationId === 'wssr_node_2' ? (
                          <LooksTwoSharpIcon sx={{ fontSize: '1.1rem' }} />
                        ) : (
                          <LooksOneSharpIcon sx={{ fontSize: '1.1rem' }} />
                        )}
                        <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, letterSpacing: '0.05em', fontWeight: 600 }}>
                          {isLive ? 'ON AIR' : 'DECK'}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: { xs: '0.7rem', md: '0.9rem' },
                          fontWeight: isLive ? 700 : 500,
                          color: isLive ? color : 'rgba(255,255,255,0.6)',
                          lineHeight: 1.3,
                          marginBottom: '2px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {eventStartTime.format('MMM D · h:mm A')}{tzAbbr ? ` ${tzAbbr}` : ''}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: isLive ? 700 : 600,
                          fontSize: { xs: '1.1rem', md: '1.3rem' },
                          color: isLive ? color : '#fff',
                          lineHeight: 1.3,
                          textAlign: 'left',
                        }}
                      >
                        {event.name}
                      </Typography>
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      )}
    </div>
  );
}

export default ScheduleCard;
